import { useEffect, useState } from "react";

/**
 * Returns true once the user has accumulated `minutes` minutes of session dwell time.
 * Persists across reloads via sessionStorage so a refresh mid-session doesn't reset the timer.
 * Used to defer non-essential "talk to sales" surfaces until the user has had time to read.
 */
export function useSessionDwell(minutes: number): boolean {
  const targetMs = minutes * 60 * 1000;
  const STORAGE_KEY = `eva_session_dwell_start_v1`;

  const computeStart = (): number => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        const n = Number(raw);
        if (Number.isFinite(n) && n > 0) return n;
      }
    } catch {}
    const now = Date.now();
    try { sessionStorage.setItem(STORAGE_KEY, String(now)); } catch {}
    return now;
  };

  const [ready, setReady] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    const start = computeStart();
    return Date.now() - start >= targetMs;
  });

  useEffect(() => {
    if (ready) return;
    const start = computeStart();
    const remaining = Math.max(0, targetMs - (Date.now() - start));
    if (remaining === 0) { setReady(true); return; }
    const t = window.setTimeout(() => setReady(true), remaining);
    return () => window.clearTimeout(t);
  }, [ready, targetMs]);

  return ready;
}

export default useSessionDwell;